import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { testimonials, aboutInfo } from '@/mocks/articles';

export default function About() {
  const navigate = useNavigate();
  const baseUrl = typeof window !== 'undefined' ? window.location.origin + (window as any).__BASE_PATH__ : '';
  const canonicalUrl = baseUrl + '/about';

  const orgSchema = {
    name: aboutInfo.brandName,
    url: baseUrl + '/',
    description: aboutInfo.intro,
    contactPoint: {
      '@type': 'ContactPoint',
      email: aboutInfo.contact.email,
      contactType: 'customer service',
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: aboutInfo.contact.address,
      addressLocality: '广州',
      addressRegion: '广东',
      addressCountry: 'CN',
    },
  };

  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormState('success');
      return;
    }
    setFormState('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9s8mg7nahb7382ovs7g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        if (msg.includes('spam') || msg.includes('form data is spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(msg);
        }
        setFormState('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormState('error');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 麻将竞技资讯"
        description="麻将竞技资讯平台品牌介绍、口碑评价与联系方式。传播麻将文化，分享竞技技巧，让每一位麻将爱好者都能在健康环境中享受竞技乐趣。"
        keywords="关于我们,麻将竞技资讯,品牌介绍,麻将平台,联系方式"
        canonicalUrl={canonicalUrl}
      />
      <SchemaOrg type="Organization" data={orgSchema} />

      <Navbar />

      <main className="pt-16">
        <section className="bg-background-100 py-10 md:py-14">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h1 className="font-heading text-2xl md:text-3xl font-black text-foreground-950 mb-6">品牌介绍</h1>
            <div className="bg-background-100 rounded-xl border border-background-200/70 p-6 md:p-8 mb-10">
              <p className="text-foreground-700 leading-relaxed mb-4">{aboutInfo.intro}</p>
              <p className="text-foreground-700 leading-relaxed">{aboutInfo.mission}</p>
            </div>

            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4">核心价值观</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              {aboutInfo.values.map((val, idx) => (
                <div key={idx} className="bg-accent-50 border border-accent-200/50 rounded-lg p-4 text-center">
                  <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
                    <i className={`${['ri-shield-check-line', 'ri-medal-line', 'ri-heart-pulse-line', 'ri-book-open-line'][idx]} text-lg`}></i>
                  </div>
                  <span className="font-heading font-semibold text-sm text-foreground-900 whitespace-nowrap">{val}</span>
                </div>
              ))}
            </div>

            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4">用户口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
              {testimonials.map((t, idx) => (
                <div key={idx} className="bg-background-100 rounded-xl border border-background-200/70 p-5">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${i < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">{t.content}</p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-bold text-primary-600">{t.name[0]}</span>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4">联系我们</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-background-100 rounded-xl border border-background-200/70 p-6 md:p-8">
                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">电子邮箱</div>
                      <div className="text-sm text-foreground-600">{aboutInfo.contact.email}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 flex-shrink-0">
                      <i className="ri-wechat-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">微信公众号</div>
                      <div className="text-sm text-foreground-600">{aboutInfo.contact.wechat}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 flex-shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">办公地址</div>
                      <div className="text-sm text-foreground-600">{aboutInfo.contact.address}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-xl border border-background-200/70 p-6 md:p-8">
                <h3 className="font-heading font-semibold text-lg text-foreground-950 mb-4">发送消息</h3>

                {formState === 'success' ? (
                  <div className="text-center py-8">
                    <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                      <i className="ri-check-line text-2xl"></i>
                    </div>
                    <p className="font-medium text-foreground-900 mb-1">提交成功！</p>
                    <p className="text-sm text-foreground-500">感谢你的留言，我们会尽快回复。</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-readdy-form="">
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="form-honeypot"
                    />
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1">姓名</label>
                        <input
                          id="about-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 text-foreground-900"
                          placeholder="请输入你的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1">邮箱</label>
                        <input
                          id="about-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 text-foreground-900"
                          placeholder="请输入你的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1">留言内容</label>
                        <textarea
                          id="about-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 text-foreground-900 resize-none"
                          placeholder="请输入你的留言内容（最多500字）"
                        ></textarea>
                      </div>
                      {formState === 'error' && formError && (
                        <p className="text-sm text-primary-600">{formError}</p>
                      )}
                      <button
                        type="submit"
                        disabled={formState === 'submitting'}
                        className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-background-50 font-semibold rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {formState === 'submitting' ? '提交中...' : '发送消息'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}